import { markRaw, reactive } from 'vue'

export type ConversationCommand = 'response' | 'notes' | 'mutation' | 'fork' | 'recovery'

export interface ConversationOperation {
  readonly epoch: number
  readonly signal: AbortSignal
  isCurrent: () => boolean
  isCancelled: () => boolean
  onStop: (stop: () => void) => void
  finish: () => void
}

/** One local operation owner. Durable leases, receipts and transactions stay in the surface. */
export function createConversationSession() {
  const state = reactive<{ epoch: number; command: ConversationCommand | null }>({
    epoch: 0,
    command: null,
  })
  let active: {
    controller: AbortController
    operation: ConversationOperation
    cleanups: (() => void)[]
  } | null = null

  function begin(command: ConversationCommand): ConversationOperation | null {
    if (active) return null
    const controller = new AbortController()
    const epoch = state.epoch
    const cleanups: (() => void)[] = []
    const operation: ConversationOperation = {
      epoch,
      signal: controller.signal,
      isCurrent: () => epoch === state.epoch && active?.operation === operation,
      isCancelled: () =>
        controller.signal.aborted || epoch !== state.epoch || active?.operation !== operation,
      onStop(stop) {
        if (controller.signal.aborted) {
          stop()
          return
        }
        controller.signal.addEventListener('abort', stop, { once: true })
        cleanups.push(() => controller.signal.removeEventListener('abort', stop))
      },
      finish() {
        cleanups.forEach((cleanup) => cleanup())
        if (active?.operation !== operation) return
        active = null
        state.command = null
      },
    }
    active = { controller, operation, cleanups }
    state.command = command
    return operation
  }

  function stop() {
    active?.controller.abort()
  }

  function dispose() {
    stop()
    active?.operation.finish()
    state.epoch += 1
  }

  return markRaw({
    state,
    begin,
    stop,
    dispose,
    get busy() {
      return state.command !== null
    },
  })
}

export type StoryConversationSession = ReturnType<typeof createConversationSession>

/** Ordinary change coordination. The surface owns the durable operation and
 * installs its confirmed timeline; stale work cannot install or report errors. */
export async function runConversationChange<T>(
  session: StoryConversationSession,
  persist: () => Promise<T>,
  install: (result: T) => void | Promise<void>,
  onError: (cause: unknown) => void | Promise<void>
): Promise<boolean> {
  const operation = session.begin('mutation')
  if (!operation) return false
  try {
    const result = await persist()
    if (!operation.isCurrent()) return false
    await install(result)
    return true
  } catch (cause) {
    if (operation.isCurrent()) await onError(cause)
    return false
  } finally {
    operation.finish()
  }
}
